import { useState } from "react";
import { Home, Bookmark, Mail } from "lucide-react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import MessagePopup from "../UIFeedback/MessagePopup/MessagePopup";

const Footer = () => {
  const { t } = useTranslation();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const notifications = useSelector((state) => state.messages.notifications);
  const totalUnread = Object.values(notifications).reduce((sum, count) => sum + count, 0);
  const [popupContext, setPopupContext] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  const isInActiveChat = location.pathname.startsWith("/messages/");

  const navLinks = [
    { id: 1, icon: Home, label: t("home"), path: "/" },
    { id: 3, icon: Bookmark, label: t("saved", "Saved"), path: "/favorites" },
    { id: 5, icon: Mail, label: t("messages"), path: "/messages" },
  ];

  const handleMessagesClick = (e) => {
    if (!isAuthenticated) {
      e.preventDefault();
      setPopupContext("messages");
      setTimeout(() => setPopupContext(null), 2500);
    } else {
      navigate("/messages");
    }
  };
  
  const handleFavoritesClick = (e) => {
    if (!isAuthenticated) {
      e.preventDefault();
      setPopupContext("favorites");
      setTimeout(() => setPopupContext(null), 3500);
    } else {
      navigate("/favorites");
    }
  };

  const handleHomeClick = (e) => {
    // If already on home page, scroll to top instead of navigating
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Hide footer when user is in an active chat to avoid keyboard issues on mobile
  const shouldShow = !isInActiveChat;

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.footer 
          className="fixed bottom-0 left-0 right-0 w-full z-50 pointer-events-none pb-[env(safe-area-inset-bottom)]"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
        >
          {/* Gradient fade for visual transition - accounts for safe area on notched devices */}
          <div className="pt-4 pb-2 px-4 bg-gradient-to-t from-white via-white to-transparent">
            {/* Navigation pill container */}
            <nav className="
              bg-white/80 backdrop-blur-md 
              border border-border/50 
              rounded-full 
              px-4 sm:px-6 
              py-2 sm:py-3
              flex justify-center items-center gap-6 sm:gap-8 md:gap-12
              shadow-lg 
              pointer-events-auto
              w-fit mx-auto
            ">
              {navLinks.map(({ id, icon: Icon, label, path }) => (
                <NavLink
                  key={id}
                  to={path}
                  className={({ isActive }) => `
                    flex flex-col items-center gap-0.5 sm:gap-1
                    w-[68px] h-[50px] sm:w-[80px] sm:h-[60px] 
                    justify-center 
                    rounded-[25px] sm:rounded-[30px]
                    transition-all duration-200
                    hover:bg-muted
                    relative
                    ${isActive ? 'text-primary' : 'text-muted-foreground'}
                  `}
                  aria-label={label}
                  onClick={
                    path === "/messages"
                      ? handleMessagesClick
                      : path === "/favorites"
                      ? handleFavoritesClick
                      : path === "/"
                      ? handleHomeClick
                      : null
                  }
                >
                  {({ isActive }) => (
                    <>
                      <div className="relative">
                        <Icon 
                          className={`
                            w-5 h-5 sm:w-6 sm:h-6 
                            transition-all duration-200
                            ${isActive ? 'text-primary scale-110' : 'text-muted-foreground'}
                          `}
                        />
                        {/* Unread badge for messages */}
                        {path === "/messages" && totalUnread > 0 && isAuthenticated && (
                          <span className="
                            absolute -top-2 -right-3
                            bg-destructive text-destructive-foreground
                            text-[10px] sm:text-xs font-medium
                            min-w-[22px] h-[22px] px-1
                            flex items-center justify-center
                            rounded-full
                            border-2 border-white
                            shadow-sm
                          ">
                            {totalUnread > 9 ? '9+' : totalUnread}
                          </span>
                        )}
                      </div>
                      {/* Label - hidden on very small screens, shown from sm up */}
                      <span className={`
                        text-[10px] sm:text-xs
                        text-center
                        whitespace-nowrap
                        transition-colors duration-200
                        ${isActive ? 'text-primary font-medium' : 'text-muted-foreground'}
                      `}>
                        {label}
                      </span>
                      {/* Active indicator dot */}
                      {isActive && (
                        <div className="
                          absolute -bottom-0.5
                          w-1 h-1 sm:w-1.5 sm:h-1.5
                          bg-primary 
                          rounded-full
                        " />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Tooltip for Messages/Favorites when not logged in */}
          <MessagePopup show={!!popupContext} message={
            <>
              <button 
                onClick={() => navigate("/login")} 
                className="bg-transparent border-none text-primary underline cursor-pointer text-inherit px-1"
              >
                {t("login_button")}
              </button>
              {" / "}
              <button 
                onClick={() => navigate("/sign-up")} 
                className="bg-transparent border-none text-primary underline cursor-pointer text-inherit px-1"
              >
                {t("sign-up")}
              </button>
              {" "}{t("to_action")}{" "}
              {popupContext === "messages"
                ? t("view_messages_action")
                : popupContext === "favorites"
                ? t("view_favorites_action")
                : ""}
            </>
          } />
        </motion.footer>
      )}
    </AnimatePresence>
  );
};

export default Footer;
